import { formatEventDate, formatUploadDate } from "@/lib/schemaBuilderHelpers";
import { getSchemaActivity } from "@/services/schemaBuilderApi";
import type {
  SchemaActivityEvent,
  SchemaActivityEventType,
  SchemaMappedFile,
} from "@/types/schemaBuilder";
import {
  BookOpenCheck,
  ChevronDown,
  ChevronUp,
  File,
  FileText,
  Files,
  Pencil,
  Upload,
} from "lucide-react";
import { type ElementType, type ReactNode, useEffect, useMemo, useState } from "react";

interface Props {
  schemaId: string | null;
  schemaName: string;
  localActivity?: SchemaActivityEvent[];
  mappedFiles?: SchemaMappedFile[];
}

interface EventMeta {
  Icon: ElementType;
  label: (schemaName: string, detail?: string) => ReactNode;
}

const ICON_CLASS = "h-3.5 w-3.5 text-[var(--color-text-secondary)]";

const EVENT_META: Record<SchemaActivityEventType, EventMeta> = {
  schema_created: {
    Icon: Upload,
    label: (n, detail) => (
      <>
        created <strong>{n}</strong>
        {detail ? ` ${detail}` : ""}
      </>
    ),
  },
  schema_renamed: {
    Icon: Pencil,
    label: (_, detail) => (
      <>
        changed name to <strong>{detail}</strong>
      </>
    ),
  },
  schema_published: {
    Icon: BookOpenCheck,
    label: (n) => (
      <>
        published <strong>{n}</strong>
      </>
    ),
  },
  schema_archived: {
    Icon: Files,
    label: (n) => (
      <>
        archived <strong>{n}</strong>
      </>
    ),
  },
  column_added: {
    Icon: Pencil,
    label: (n) => (
      <>
        modified <strong>{n}</strong>
      </>
    ),
  },
  column_edited: {
    Icon: Pencil,
    label: (n) => (
      <>
        modified <strong>{n}</strong>
      </>
    ),
  },
  column_deleted: {
    Icon: Pencil,
    label: (n) => (
      <>
        modified <strong>{n}</strong>
      </>
    ),
  },
  rule_added: {
    Icon: Pencil,
    label: (n) => (
      <>
        modified <strong>{n}</strong>
      </>
    ),
  },
  rule_deleted: {
    Icon: Pencil,
    label: (n) => (
      <>
        modified <strong>{n}</strong>
      </>
    ),
  },
  reference_list_added: {
    Icon: Pencil,
    label: (n) => (
      <>
        modified <strong>{n}</strong>
      </>
    ),
  },
  reference_list_updated: {
    Icon: Pencil,
    label: (n) => (
      <>
        modified <strong>{n}</strong>
      </>
    ),
  },
  reference_list_deleted: {
    Icon: Pencil,
    label: (n) => (
      <>
        modified <strong>{n}</strong>
      </>
    ),
  },
  draft_saved: {
    Icon: FileText,
    label: (n) => (
      <>
        saved draft of <strong>{n}</strong>
      </>
    ),
  },
  file_uploaded: {
    Icon: Upload,
    label: (_, detail) => (
      <>
        uploaded file <strong>{detail}</strong>
      </>
    ),
  },
};

interface UploadGroup {
  sessionKey: string;
  files: SchemaMappedFile[];
  uploadedAt: string;
  uploadedBy: string;
}

function groupMappedFiles(files: SchemaMappedFile[]): UploadGroup[] {
  const map = new Map<string, SchemaMappedFile[]>();
  for (const f of files) {
    const key = f.mappedAt.slice(0, 16);
    const existing = map.get(key);
    if (existing) existing.push(f);
    else map.set(key, [f]);
  }
  return Array.from(map.entries())
    .map(([sessionKey, groupFiles]) => ({
      sessionKey,
      files: groupFiles,
      uploadedAt: groupFiles[0]?.mappedAt ?? "",
      uploadedBy: groupFiles[0]?.uploadedBy ?? "—",
    }))
    .sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());
}

function UploadGroupRow({ group }: { group: UploadGroup }) {
  const [expanded, setExpanded] = useState(false);
  const isMulti = group.files.length > 1;

  return (
    <div className="overflow-hidden rounded-md border border-[var(--color-border-light)] bg-white">
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="shrink-0">
          {isMulti ? (
            <Files className="h-5 w-5 text-[var(--color-text-secondary)]" />
          ) : (
            <File className="h-5 w-5 text-[var(--color-text-secondary)]" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          {isMulti ? (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="flex items-center gap-1 text-sm text-[var(--color-text-primary)]"
            >
              Multiple Files ({group.files.length})
              {expanded ? (
                <ChevronUp className="h-3.5 w-3.5" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5" />
              )}
            </button>
          ) : (
            <p className="truncate text-sm text-[var(--color-text-primary)]">
              {group.files[0]?.fileName}
            </p>
          )}
        </div>

        <span className="shrink-0 text-sm text-[var(--color-text-secondary)]">
          {formatUploadDate(group.uploadedAt)}
        </span>

        <span className="shrink-0 text-sm text-[var(--color-text-secondary)]">
          {group.uploadedBy}
        </span>
      </div>

      {isMulti && expanded && (
        <>
          {group.files.map((f) => (
            <div
              key={f.fileName + f.mappedAt}
              className="border-t border-[var(--color-border-light)] px-4 py-2.5"
            >
              <p className="text-sm text-[var(--color-text-secondary)]">{f.fileName}</p>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

export function SchemaBuilderActivityTab({
  schemaId,
  schemaName,
  localActivity = [],
  mappedFiles = [],
}: Props) {
  const [backendActivities, setBackendActivities] = useState<SchemaActivityEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!schemaId) return;
    setIsLoading(true);
    getSchemaActivity(schemaId)
      .then(setBackendActivities)
      .catch((err: Error) => setError(err.message))
      .finally(() => setIsLoading(false));
  }, [schemaId]);

  const allActivities = useMemo<SchemaActivityEvent[]>(() => {
    const seen = new Set<string>();
    return [...localActivity, ...backendActivities]
      .filter((e) => {
        if (seen.has(e.id)) return false;
        seen.add(e.id);
        return true;
      })
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }, [localActivity, backendActivities]);

  const uploadGroups = useMemo(() => groupMappedFiles(mappedFiles), [mappedFiles]);
  const totalUploads = uploadGroups.length;

  return (
    <div className="grid grid-cols-[2fr_3fr] gap-6">
      <div>
        <h3 className="mb-4 text-sm font-semibold text-[var(--color-text-primary)]">
          Version History
        </h3>

        {isLoading && (
          <p className="text-sm text-[var(--color-text-tertiary)]">Loading activity…</p>
        )}
        {error && <p className="text-sm text-red-500">{error}</p>}
        {!isLoading && !error && allActivities.length === 0 && (
          <p className="text-sm text-[var(--color-text-tertiary)]">
            {schemaId ? "No activity recorded yet." : "Add columns to start tracking activity."}
          </p>
        )}

        {!isLoading && allActivities.length > 0 && (
          <div>
            {allActivities.map((event, idx) => {
              const meta = EVENT_META[event.type];
              const { Icon } = meta;
              const isLast = idx === allActivities.length - 1;

              return (
                <div key={event.id} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[var(--color-border-light)] bg-white">
                      <Icon className={ICON_CLASS} />
                    </div>
                    {!isLast && <div className="mt-1 w-px flex-1 bg-[var(--color-border-light)]" />}
                  </div>

                  <div className={`min-w-0 ${isLast ? "pb-0" : "pb-7"}`}>
                    <p className="text-sm text-[var(--color-text-primary)]">
                      <span className="font-semibold">{event.actor}</span>{" "}
                      {meta.label(schemaName, event.detail)}
                    </p>
                    <p className="mt-0.5 text-xs text-[var(--color-text-tertiary)]">
                      {formatEventDate(event.timestamp)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <h3 className="mb-4 text-sm font-semibold text-[var(--color-text-primary)]">
          Upload Activity
        </h3>

        <p className="mb-3 text-sm text-[var(--color-text-secondary)]">
          Total uploads{" "}
          <span className="font-semibold text-[var(--color-text-primary)]">{totalUploads}</span>
        </p>

        {uploadGroups.length === 0 ? (
          <div className="rounded-md border border-[var(--color-border-light)] bg-white px-6 py-12 text-center">
            <p className="text-sm text-[var(--color-text-secondary)]">
              Your schema has no recorded upload activity.
            </p>
            <p className="mt-1 text-sm text-[var(--color-text-tertiary)]">
              After you publish your schema, any files that you remap using this schema as the
              target will appear here.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {uploadGroups.map((group) => (
              <UploadGroupRow key={group.sessionKey} group={group} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
